import type {
  EventSelectionMap,
  KeyEventDescriptor,
  KeyEventSelection,
  PrimaryEventMap,
} from '../models/conversion.types'

import type { IntegrationAccountDto } from '@/features/integrations'

function normalizeToken(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toLowerCase()
  return normalized || null
}

export function normalizeEventList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return [...new Set(value.map(normalizeToken).filter((item): item is string => item !== null))]
}

export function normalizeEventMap(
  value: EventSelectionMap | null | undefined,
  accountId: string,
): Record<string, string[]> {
  if (Array.isArray(value)) return { [accountId]: normalizeEventList(value) }
  if (!value || typeof value !== 'object') return {}
  return Object.fromEntries(
    Object.entries(value).map(([id, events]) => [id, normalizeEventList(events)]),
  )
}

export function normalizePrimaryEventMap(
  value: PrimaryEventMap | null | undefined,
  accountId: string,
): Record<string, string | null> {
  if (Array.isArray(value)) return { [accountId]: normalizeToken(value[0]) }
  if (!value || typeof value !== 'object') return {}
  return Object.fromEntries(Object.entries(value).map(([id, event]) => [id, normalizeToken(event)]))
}

export function accountIdentity(
  account: IntegrationAccountDto,
  descriptor: KeyEventDescriptor,
): string {
  const config = account.integration_config ?? {}
  const configured = descriptor.idKey
    ? config[descriptor.idKey]
    : config[`${descriptor.platform}_account_id`]
  return String(configured ?? account.id)
}

export function accountLabel(account: IntegrationAccountDto): string {
  const config = account.integration_config ?? {}
  const label = config.app_name ?? config.name ?? config.label ?? account.name ?? account.id
  return String(label)
}

export function selectionForAccount(
  accountId: string,
  events: EventSelectionMap | null | undefined,
  primary: PrimaryEventMap | null | undefined,
): KeyEventSelection {
  return {
    events: normalizeEventMap(events, accountId)[accountId] ?? [],
    primary: normalizePrimaryEventMap(primary, accountId)[accountId] ?? null,
  }
}

/** The backend has returned both a bare array and an account-id map; numeric ids stringify to the
 * same object key, which preserves the legacy `data[id] || data[Number(id)]` tolerance. */
export function liveEventsForAccount(payload: unknown, accountId: string): string[] {
  if (Array.isArray(payload)) return normalizeEventList(payload)
  if (!payload || typeof payload !== 'object') return []
  const value = (payload as Record<string, unknown>)[accountId]
  return normalizeEventList(value)
}

export function buildKeyEventValues(input: {
  descriptor: KeyEventDescriptor
  accountId: string
  selection: KeyEventSelection
  events: EventSelectionMap | null
  primary: PrimaryEventMap | null
}): {
  events: Record<string, string[]>
  primary: Record<string, string | null>
  ga4Set?: true
} {
  const events = normalizeEventMap(input.events, input.accountId)
  const primary = normalizePrimaryEventMap(input.primary, input.accountId)
  events[input.accountId] = normalizeEventList(input.selection.events)
  primary[input.accountId] = normalizeToken(input.selection.primary)

  const result: {
    events: Record<string, string[]>
    primary: Record<string, string | null>
    ga4Set?: true
  } = { events, primary }

  if (input.descriptor.platform === 'ga4') result.ga4Set = true
  return result
}

export function eventLabel(value: string | null): string {
  if (!value) return ''
  return value
    .replace(/^twitter_stats_/u, '')
    .split(/[_\s-]+/u)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ')
}
